import React, { useCallback, useEffect, useState } from 'react';
import aiApi from '../../utils/aiApi';
import './aiSettingsAdmin.css';

/**
 * AiSettingsAdmin
 * Admin controls for the AI Assistant: enable/disable, per-task model routing,
 * default/fallback models and usage limits.
 */

const TASKS = [
  ['summary', 'Summary'],
  ['reply', 'Smart Reply'],
  ['translate', 'Translation'],
  ['grammar', 'Grammar'],
  ['rewrite', 'Rewrite'],
  ['improve', 'Improve Writing'],
  ['continue', 'Continue Writing'],
  ['subject', 'Subject Generator'],
  ['security', 'Phishing / Security'],
  ['tasks', 'Task Extraction'],
  ['meeting', 'Meeting Extraction'],
  ['calendar', 'Calendar Detection'],
  ['attachment', 'Attachment Summary'],
  ['sentiment', 'Sentiment'],
  ['compose', 'AI Email Composer'],
];

const MODEL_SUGGESTIONS = [
  'openai/gpt-5-mini',
  'openai/gpt-5',
  'anthropic/claude-sonnet-4',
  'google/gemini-2.5-flash',
  'deepseek/deepseek-chat-v3',
  'google/gemma-4-31b-it:free',
  'meta-llama/llama-3.3-70b-instruct:free',
  'openai/gpt-oss-20b:free',
  'qwen/qwen3-next-80b-a3b-instruct:free',
  'openrouter/free',
];

const DEFAULT_SETTINGS = {
  enabled: true,
  configured: false,
  defaultModel: 'google/gemma-4-31b-it:free',
  fallbackModel: 'deepseek/deepseek-chat-v3',
  modelRoutes: {
    summary: 'google/gemma-4-31b-it:free',
    reply: 'meta-llama/llama-3.3-70b-instruct:free',
    translate: 'google/gemini-2.5-flash',
    grammar: 'openai/gpt-5-mini',
    rewrite: 'anthropic/claude-sonnet-4',
    improve: 'openai/gpt-5-mini',
    continue: 'openai/gpt-5',
    subject: 'openai/gpt-oss-20b:free',
    security: 'meta-llama/llama-3.3-70b-instruct:free',
    tasks: 'google/gemma-4-31b-it:free',
    meeting: 'google/gemma-4-31b-it:free',
    calendar: 'google/gemma-4-31b-it:free',
    attachment: 'google/gemma-4-31b-it:free',
    sentiment: 'openai/gpt-oss-20b:free',
    compose: 'meta-llama/llama-3.3-70b-instruct:free',
  },
  dailyTokenLimit: 2000000,
  perUserTokenLimit: 200000,
  maxResponseTokens: 1500,
  cacheTtlSeconds: 3600,
  todayUsage: { tokens: 0, cost: 0, calls: 0 },
};

function normalize(data = {}) {
  return {
    ...DEFAULT_SETTINGS,
    ...data,
    modelRoutes: { ...DEFAULT_SETTINGS.modelRoutes, ...(data.modelRoutes || {}) },
    todayUsage: data.todayUsage || DEFAULT_SETTINGS.todayUsage,
  };
}

export default function AiSettingsAdmin() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await aiApi.getAdminSettings();
      if (!res?.success) throw new Error(res?.message || 'Failed to load AI settings');
      setSettings(normalize(res.data));
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load AI settings');
      // Keep defaults so the form + Save button stay visible.
      setSettings((prev) => prev || DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const update = (patch) => setSettings((prev) => ({ ...prev, ...patch }));
  const updateRoute = (task, model) =>
    setSettings((prev) => ({ ...prev, modelRoutes: { ...prev.modelRoutes, [task]: model } }));

  const save = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const payload = {
        enabled: settings.enabled,
        defaultModel: settings.defaultModel,
        fallbackModel: settings.fallbackModel,
        modelRoutes: settings.modelRoutes,
        dailyTokenLimit: Number(settings.dailyTokenLimit) || 0,
        perUserTokenLimit: Number(settings.perUserTokenLimit) || 0,
        maxResponseTokens: Number(settings.maxResponseTokens) || 0,
        cacheTtlSeconds: Number(settings.cacheTtlSeconds) || 0,
      };
      const res = await aiApi.updateAdminSettings(payload);
      if (!res?.success) throw new Error(res?.message || 'Save failed');
      setMessage('AI settings saved.');
      await loadSettings();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const usage = settings.todayUsage || { tokens: 0, cost: 0, calls: 0 };
  const tokenCount = Number(usage.tokens) || 0;
  const callCount = Number(usage.calls) || 0;
  const costValue = Number(usage.cost) || 0;

  return (
    <section className="admin-card ai-admin" id="ai-settings">
      <div className="admin-section-title">
        <h3>AI Assistant</h3>
        <div className="ai-admin-head-actions">
          <label className="ai-admin-switch">
            <input
              type="checkbox"
              checked={Boolean(settings.enabled)}
              onChange={(e) => update({ enabled: e.target.checked })}
              disabled={loading}
            />
            <span>{settings.enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
          <button type="button" className="ai-admin-reload" onClick={loadSettings} disabled={loading}>
            {loading ? 'Loading…' : 'Reload'}
          </button>
        </div>
      </div>

      {!settings.configured && !loading && (
        <div className="admin-alert error">
          OPENROUTER_API_KEY is not set on the server. Add it to backend/.env and restart.
        </div>
      )}
      {message && <div className="admin-alert success">{message}</div>}
      {error && <div className="admin-alert error">{error}</div>}

      <div className="ai-admin-usage">
        <div>
          <span>Calls today</span>
          <strong>{callCount}</strong>
        </div>
        <div>
          <span>Tokens today</span>
          <strong>{tokenCount.toLocaleString()}</strong>
        </div>
        <div>
          <span>Est. cost today</span>
          <strong>${costValue.toFixed(4)}</strong>
        </div>
      </div>

      <datalist id="ai-model-suggestions">
        {MODEL_SUGGESTIONS.map((m) => (
          <option key={m} value={m} />
        ))}
      </datalist>

      <div className="ai-admin-grid">
        <label className="ai-admin-field">
          <span>Default model</span>
          <input
            list="ai-model-suggestions"
            value={settings.defaultModel || ''}
            onChange={(e) => update({ defaultModel: e.target.value })}
          />
        </label>
        <label className="ai-admin-field">
          <span>Global fallback model</span>
          <input
            list="ai-model-suggestions"
            value={settings.fallbackModel || ''}
            onChange={(e) => update({ fallbackModel: e.target.value })}
          />
        </label>
        <label className="ai-admin-field">
          <span>Daily token limit (workspace)</span>
          <input
            type="number"
            min="0"
            value={settings.dailyTokenLimit}
            onChange={(e) => update({ dailyTokenLimit: e.target.value })}
          />
        </label>
        <label className="ai-admin-field">
          <span>Per-user daily token limit</span>
          <input
            type="number"
            min="0"
            value={settings.perUserTokenLimit}
            onChange={(e) => update({ perUserTokenLimit: e.target.value })}
          />
        </label>
        <label className="ai-admin-field">
          <span>Max response tokens</span>
          <input
            type="number"
            min="0"
            value={settings.maxResponseTokens}
            onChange={(e) => update({ maxResponseTokens: e.target.value })}
          />
        </label>
        <label className="ai-admin-field">
          <span>Cache TTL (seconds)</span>
          <input
            type="number"
            min="0"
            value={settings.cacheTtlSeconds}
            onChange={(e) => update({ cacheTtlSeconds: e.target.value })}
          />
        </label>
      </div>

      <h4 className="ai-admin-subtitle">Model routing per task</h4>
      <p className="ai-admin-hint">
        Choose the OpenRouter model for each task. Leave blank to use the default model.
      </p>
      <div className="ai-admin-routes">
        {TASKS.map(([task, label]) => (
          <label key={task} className="ai-admin-field">
            <span>{label}</span>
            <input
              list="ai-model-suggestions"
              value={settings.modelRoutes[task] || ''}
              placeholder={settings.defaultModel}
              onChange={(e) => updateRoute(task, e.target.value)}
            />
          </label>
        ))}
      </div>

      <div className="ai-admin-actions">
        <button
          type="button"
          className="ai-admin-save"
          onClick={save}
          disabled={saving || loading}
        >
          {saving ? 'Saving…' : 'Save AI settings'}
        </button>
      </div>
    </section>
  );
}
